import { useState } from 'react';
import dayjs from 'dayjs';
import { CalendarType } from '@demind-inc/core';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  calendarApi,
  defaultOutlookCalScopes,
  exchangeDesktopCodeToToken,
  eventsSnackBarAtom,
  fetchGoogleUser,
  getWeekEdgesDateOf,
  googleCalAuth,
  captureGoogleAuthError,
  syncedDatesState,
  useAuthContext,
  useSyncAppleCalendarsList,
  useSubscribeGoogleCalendarWatch,
  useSubscribeOutlookCalendarWatch,
  useSyncGCalendarsList,
  useSyncGoogleCalendarEvents,
  useSyncOutlookCalendarEvents,
  useSyncOutlookCalendarsList,
  transformExpiryDate,
} from '../data-access';
import {
  fetchAppleCalendarsFromDesktop,
  getAppleCalendarSyncWindow,
  isAppleCalendarDesktopSupported,
  openAppleCalendarSettings,
} from '../helpers';
import { trackEventMixpanel } from '../utils';
import { isDesktopApp } from '../utils/environment';

const DESKTOP_GOOGLE_CALENDAR_SCOPES = [
  'openid',
  'email',
  'profile',
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/calendar.readonly',
];
const DESKTOP_OUTLOOK_CALLBACK_ORIGIN =
  process.env.REACT_APP_ENV === 'staging'
    ? 'https://staging.lifestack.ai'
    : 'https://app.lifestack.ai';
const DESKTOP_OUTLOOK_CALLBACK_URL = `${DESKTOP_OUTLOOK_CALLBACK_ORIGIN}/callback/outlook`;

export const useConnectCalendar = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuthContext();
  const syncedDates = useRecoilValue(syncedDatesState);
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);
  const { syncAppleCalendarsList } = useSyncAppleCalendarsList();
  const { syncGCalendarsList } = useSyncGCalendarsList();
  const { subscribeGoogleCalendarWatch } = useSubscribeGoogleCalendarWatch();
  const { syncGoogleCalEvents } = useSyncGoogleCalendarEvents();
  const { syncOutlookCalendarsList } = useSyncOutlookCalendarsList();
  const { subscribeOutlookCalendarWatch } = useSubscribeOutlookCalendarWatch();
  const { syncOutlookCalEvents } = useSyncOutlookCalendarEvents();
  const hasNativeGoogleDesktopAuth = typeof window.lifestack?.startGoogleDesktopAuth === 'function';
  const hasNativeOutlookDesktopAuth =
    typeof window.lifestack?.startOutlookDesktopAuth === 'function';
  const isFileProtocol = window.location.protocol === 'file:';
  const shouldUseNativeGoogleDesktopAuth =
    isDesktopApp() || (isFileProtocol && hasNativeGoogleDesktopAuth);
  const shouldUseNativeOutlookDesktopAuth =
    isDesktopApp() || (isFileProtocol && hasNativeOutlookDesktopAuth);

  const syncGoogleCalendarWeek = (userId: string, calendarIds: string[]) => {
    const { startDate, endDate } = getWeekEdgesDateOf(dayjs());

    void syncGoogleCalEvents({
      userId,
      calendarIds,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
    });
    void subscribeGoogleCalendarWatch({
      calendarIds,
      userId,
    });
  };

  const syncOutlookCalendarWeek = (userId: string, calendarIds: string[]) => {
    const { startDate, endDate } = getWeekEdgesDateOf(dayjs());

    void syncOutlookCalEvents({
      userId,
      calendarIds,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
    });
    void subscribeOutlookCalendarWatch({
      calendarIds,
      userId,
    });
  };

  const connectCal = async (type: CalendarType, redirectUrl?: string): Promise<boolean> => {
    try {
      setError('');
      setIsLoading(true);

      if (type === 'google') {
        if (shouldUseNativeGoogleDesktopAuth) {
          if (!hasNativeGoogleDesktopAuth) {
            throw new Error('Google Calendar desktop auth is not available in this build.');
          }
          if (!user.userId) {
            throw new Error('You need to be signed in before connecting a calendar.');
          }

          const desktopAuth = await window.lifestack.startGoogleDesktopAuth({
            flow: 'calendar_connect',
            scopes: DESKTOP_GOOGLE_CALENDAR_SCOPES,
          });
          const tokens = await exchangeDesktopCodeToToken({
            serverAuthCode: desktopAuth.code,
            clientId: desktopAuth.clientId,
            clientSecret: desktopAuth.clientSecret,
            redirectUri: desktopAuth.redirectUri,
            codeVerifier: desktopAuth.codeVerifier,
          });

          if (!tokens.access_token) {
            throw new Error('Google Calendar desktop auth did not return an access token.');
          }

          const googleUser = await fetchGoogleUser({ accessToken: tokens.access_token });
          if (!googleUser.email) {
            throw new Error('Google Calendar desktop auth did not return an account email.');
          }

          const newCalendarIds = await syncGCalendarsList({
            userId: user.userId,
            rootEmail: googleUser.email,
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            expiryDate: tokens.expires_in ? transformExpiryDate(tokens.expires_in) : undefined,
            scopes: desktopAuth.scopes,
          });

          syncGoogleCalendarWeek(user.userId, newCalendarIds);
        } else {
          if (isFileProtocol) {
            throw new Error(
              'Google Calendar desktop auth is unavailable. The desktop app cannot use the web redirect flow from a file:// origin.'
            );
          }

          await googleCalAuth.signinRedirect({ url_state: redirectUrl });
        }
      } else if (type === 'outlook') {
        if (shouldUseNativeOutlookDesktopAuth) {
          if (!hasNativeOutlookDesktopAuth) {
            throw new Error('Outlook Calendar desktop auth is not available in this build.');
          }
          if (!user.userId) {
            throw new Error('You need to be signed in before connecting a calendar.');
          }

          const state =
            window.crypto?.randomUUID?.() ??
            `${Date.now()}_${Math.random().toString(36).slice(2)}`;
          const authUrl = (
            await calendarApi.generateOutlookAuthUrl(DESKTOP_OUTLOOK_CALLBACK_URL, 'v2.0')
          ).data;
          const finalUrl = new URL(authUrl);
          finalUrl.searchParams.set('state', state);
          const desktopAuth = await window.lifestack.startOutlookDesktopAuth({
            authUrl: finalUrl.toString(),
            callbackUrl: DESKTOP_OUTLOOK_CALLBACK_URL,
          });
          const callbackUrl = new URL(desktopAuth.callbackUrl);
          const returnedError = callbackUrl.searchParams.get('error');
          const code = callbackUrl.searchParams.get('code') ?? '';
          const returnedState = callbackUrl.searchParams.get('state') ?? '';

          if (returnedError) {
            throw new Error(`outlook_desktop_auth_error:${returnedError}`);
          }

          if (!code) {
            throw new Error('outlook_desktop_auth_missing_code');
          }

          if (!returnedState || returnedState !== state) {
            throw new Error('outlook_desktop_auth_state_mismatch');
          }

          const tokens = await calendarApi.exchangeOutlookToken(
            user.userId,
            code,
            DESKTOP_OUTLOOK_CALLBACK_URL,
            'v2.0',
            defaultOutlookCalScopes
          );

          const newCalendarIds = await syncOutlookCalendarsList({
            userId: user.userId,
            scopes: defaultOutlookCalScopes,
            accessToken: tokens.data.accessToken,
            refreshToken: tokens.data.refreshToken,
            expiryDate: tokens.data.expiryDate,
            rootEmail: tokens.data.rootEmail,
          });

          syncOutlookCalendarWeek(user.userId, newCalendarIds);
        } else {
          if (isFileProtocol) {
            throw new Error(
              'Outlook Calendar desktop auth is unavailable. The desktop app cannot use the web redirect flow from a file:// origin.'
            );
          }

          const redirectUri = `${window.location.origin}/callback/outlook`;
          const url = (await calendarApi.generateOutlookAuthUrl(redirectUri, 'v2.0')).data;
          const finalUrl = redirectUrl ? `${url}&state=${encodeURIComponent(redirectUrl)}` : url;
          window.location.href = finalUrl;
        }
      } else if (type === 'apple') {
        if (!isAppleCalendarDesktopSupported()) {
          throw new Error('Apple Calendar native sync is only available in the macOS desktop app.');
        }

        const { startTime, endTime } = getAppleCalendarSyncWindow(syncedDates);
        const appleCalendars = await fetchAppleCalendarsFromDesktop({ startTime, endTime });

        await syncAppleCalendarsList({
          userId: user.userId,
          email: user.email,
          metricId: user.metricId,
          userPersona: user.persona?.job,
          startTime,
          endTime,
          appleCalendars,
        });

        setEventsSnackbar({
          message: `Apple Calendar synced (${appleCalendars.length} calendars).`,
          autoHideDuration: 3000,
          type: 'success',
        });
      }
      setIsLoading(false);
      trackEventMixpanel('connect_calendar', { type });
      return true;
    } catch (err) {
      setIsLoading(false);
      if (type === 'google') {
        captureGoogleAuthError(err, 'calendar_connect', 'connect');
      }
      const rawErrorMessage = (err as Error)?.message || 'Something went wrong connecting your calendars.';
      const isPermissionIssue =
        rawErrorMessage.includes('calendar_permission_denied') ||
        rawErrorMessage.includes('calendar_permission_write_only') ||
        rawErrorMessage.toLowerCase().includes('permission');
      const canOpenSettings = isPermissionIssue && isAppleCalendarDesktopSupported();
      const snackMessage = isPermissionIssue
        ? 'Apple Calendar permission denied. Enable Calendar access in macOS Settings and try again.'
        : rawErrorMessage;

      setError(rawErrorMessage);
      setEventsSnackbar({
        message: snackMessage,
        autoHideDuration: 6000,
        type: 'error',
        ...(canOpenSettings
          ? {
              actionLabel: 'Open Settings',
              onPress: () => {
                void (async () => {
                  const opened = await openAppleCalendarSettings();
                  if (!opened) {
                    setEventsSnackbar({
                      message:
                        'Open System Settings > Privacy & Security > Calendars and enable access for Lifestack.',
                      autoHideDuration: 6000,
                      type: 'error',
                    });
                  }
                })();
              },
            }
          : {}),
      });
      return false;
    }
  };

  return { connectCal, isLoading, error };
};
